import type { QueryClient } from '@tanstack/react-query'

import defaultLogoUrl from '@/assets/logo.svg'
import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'

const defaultBrandTitle = 'Avaca AI Hub'
const brandingStorageKey = 'aihub_branding_settings'
export const defaultHubAgentName = 'Hub Agent'
export const defaultChatUserBubbleColor = '#F4F4F4'

export const publicBrandingQueryKey = ['public-branding-settings'] as const

export interface PublicBrandAssetMetadata {
  content_type?: string | null
  key?: string | null
  size?: number | null
  updated_at?: string | null
  url?: string | null
}

export type WorkspaceBackgroundVariant = 'desktop' | 'laptop' | 'mobile'

export interface PublicWorkspaceBackgroundVariant {
  focusX?: number
  focusY?: number
  height?: number
  jpegUrl?: string
  webpUrl?: string
  width?: number
}

export interface PublicWorkspaceBackground {
  baseColor: string
  enabled: boolean
  imageOpacity: number
  intensityPreset: 'clear' | 'standard' | 'subtle'
  mobileConversationMode: 'background' | 'base_color'
  variants: Partial<Record<WorkspaceBackgroundVariant, PublicWorkspaceBackgroundVariant>>
  version?: string
}

export interface PublicBrandingConfig {
  brand_assets?: Partial<Record<'favicon' | 'logo', PublicBrandAssetMetadata>>
  chat_user_bubble_color?: string | null
  description?: string | null
  favicon?: string | null
  hub_agent_name?: string | null
  hub_agent_welcome_title?: string | null
  logo?: string | null
  title?: string | null
  workspace_background?: {
    base_color?: unknown
    enabled?: unknown
    image_opacity?: unknown
    intensity_preset?: unknown
    mobile_conversation_mode?: unknown
    variants?: Partial<
      Record<
        WorkspaceBackgroundVariant,
        {
          focus_x?: unknown
          focus_y?: unknown
          height?: unknown
          jpeg_url?: unknown
          webp_url?: unknown
          width?: unknown
        }
      >
    >
    version?: unknown
  } | null
}

export interface BrandingSettings {
  chatUserBubbleColor: string
  chatUserBubbleTextColor: '#000000' | '#FFFFFF'
  description: string
  favicon: string
  hasCustomTitle: boolean
  hubAgentName: string
  hubAgentWelcomeTitle: string
  logo: string
  title: string
  workspaceBackground: PublicWorkspaceBackground | null
}

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

type TranslationFn = (key: string) => string

export const defaultBrandingSettings: BrandingSettings = {
  chatUserBubbleColor: defaultChatUserBubbleColor,
  chatUserBubbleTextColor: '#000000',
  description: '',
  favicon: defaultLogoUrl,
  hasCustomTitle: false,
  hubAgentName: defaultHubAgentName,
  hubAgentWelcomeTitle: '',
  logo: defaultLogoUrl,
  title: defaultBrandTitle,
  workspaceBackground: null,
}

const publicApiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
})

function readText(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

function readHubAgentName(value: unknown) {
  const normalized = readText(value)

  return normalized && Array.from(normalized).length <= 20 ? normalized : defaultHubAgentName
}

export function normalizeChatUserBubbleColor(value: unknown) {
  if (typeof value !== 'string') {
    return defaultChatUserBubbleColor
  }
  const normalized = value.trim()
  return /^#[0-9a-fA-F]{6}$/.test(normalized)
    ? normalized.toUpperCase()
    : defaultChatUserBubbleColor
}

function relativeLuminance(color: string) {
  const channels = [1, 3, 5].map(
    (offset) => Number.parseInt(color.slice(offset, offset + 2), 16) / 255,
  )
  const [red, green, blue] = channels.map((channel) =>
    channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
  )
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

export function resolveChatUserBubbleTextColor(backgroundColor: unknown): '#000000' | '#FFFFFF' {
  const luminance = relativeLuminance(normalizeChatUserBubbleColor(backgroundColor))
  const blackContrast = (luminance + 0.05) / 0.05
  const whiteContrast = 1.05 / (luminance + 0.05)
  return blackContrast >= whiteContrast ? '#000000' : '#FFFFFF'
}

function readPositiveNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
}

function readNormalizedPosition(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1
    ? value
    : undefined
}

function readWorkspaceBackgroundImageOpacity(value: unknown, intensityPreset: unknown) {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0.1 && value <= 0.7) {
    return value
  }

  if (intensityPreset === 'clear') return 0.45
  if (intensityPreset === 'standard') return 0.32
  if (intensityPreset === 'subtle') return 0.24
  return 0.32
}

function normalizeWorkspaceBackground(
  value: PublicBrandingConfig['workspace_background'] | PublicWorkspaceBackground | undefined,
): PublicWorkspaceBackground | null {
  if (!value || typeof value !== 'object') {
    return null
  }
  if ('baseColor' in value) {
    const normalizedValue = {
      ...value,
      imageOpacity: readWorkspaceBackgroundImageOpacity(value.imageOpacity, value.intensityPreset),
    }
    return normalizedValue.enabled ? normalizedValue : { ...normalizedValue, enabled: false }
  }

  const baseColor =
    typeof value.base_color === 'string' && /^#[0-9a-fA-F]{6}$/.test(value.base_color)
      ? value.base_color.toUpperCase()
      : '#F8FAFE'
  const intensityPreset =
    value.intensity_preset === 'clear' ||
    value.intensity_preset === 'standard' ||
    value.intensity_preset === 'subtle'
      ? value.intensity_preset
      : 'subtle'
  const mobileConversationMode =
    value.mobile_conversation_mode === 'background' ? 'background' : 'base_color'
  const imageOpacity = readWorkspaceBackgroundImageOpacity(
    value.image_opacity,
    value.intensity_preset,
  )
  const variants: PublicWorkspaceBackground['variants'] = {}

  for (const variant of ['desktop', 'laptop', 'mobile'] as const) {
    const metadata = value.variants?.[variant]
    if (!metadata || typeof metadata !== 'object') {
      continue
    }
    const webpUrl = readText(metadata.webp_url)
    const jpegUrl = readText(metadata.jpeg_url)
    if (!webpUrl && !jpegUrl) {
      continue
    }
    variants[variant] = {
      focusX: readNormalizedPosition(metadata.focus_x),
      focusY: readNormalizedPosition(metadata.focus_y),
      height: readPositiveNumber(metadata.height),
      jpegUrl,
      webpUrl,
      width: readPositiveNumber(metadata.width),
    }
  }

  return {
    baseColor,
    enabled: value.enabled === true,
    imageOpacity,
    intensityPreset,
    mobileConversationMode,
    variants,
    version: readText(value.version),
  }
}

export function readCachedBrandingSettings() {
  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    const rawValue = window.localStorage.getItem(brandingStorageKey)
    if (!rawValue) {
      return undefined
    }

    return normalizeBrandingSettings(JSON.parse(rawValue) as PublicBrandingConfig)
  } catch {
    return undefined
  }
}

export function cacheBrandingSettings(settings: BrandingSettings) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(brandingStorageKey, JSON.stringify(settings))
  } catch {
    // 浏览器禁用本地存储时不影响运行时品牌展示。
  }
}

export function normalizeBrandingSettings(
  config?: PublicBrandingConfig | BrandingSettings | null,
): BrandingSettings {
  const title = readText(config?.title)
  const logo = readText(config?.logo) ?? defaultLogoUrl
  const favicon = readText(config?.favicon) ?? defaultLogoUrl
  const hasCustomTitle =
    title !== undefined && (config && 'hasCustomTitle' in config ? config.hasCustomTitle : true)
  const chatUserBubbleColor = normalizeChatUserBubbleColor(
    config && 'chatUserBubbleColor' in config
      ? config.chatUserBubbleColor
      : config?.chat_user_bubble_color,
  )

  return {
    chatUserBubbleColor,
    chatUserBubbleTextColor: resolveChatUserBubbleTextColor(chatUserBubbleColor),
    description: readText(config?.description) ?? '',
    favicon,
    hasCustomTitle,
    hubAgentName: readHubAgentName(
      config && 'hubAgentName' in config ? config.hubAgentName : config?.hub_agent_name,
    ),
    hubAgentWelcomeTitle:
      readText(
        config && 'hubAgentWelcomeTitle' in config
          ? config.hubAgentWelcomeTitle
          : config?.hub_agent_welcome_title,
      ) ?? '',
    logo,
    title: title ?? defaultBrandTitle,
    workspaceBackground: normalizeWorkspaceBackground(
      config && 'workspaceBackground' in config
        ? (config.workspaceBackground ?? undefined)
        : (config?.workspace_background ?? undefined),
    ),
  }
}

export function resolveBrandAssetSrc(value?: string | null, fallback = defaultLogoUrl) {
  const normalized = value?.trim()

  if (!normalized) {
    return fallback
  }

  if (
    normalized.startsWith('data:') ||
    normalized.startsWith('http://') ||
    normalized.startsWith('https://') ||
    normalized.startsWith('/')
  ) {
    return normalized
  }

  return `data:image/png;base64,${normalized}`
}

export async function getPublicBrandingSettings() {
  const response =
    await publicApiClient.request<SuccessEnvelope<PublicBrandingConfig>>('/app/sys-settings')

  return normalizeBrandingSettings(response.data)
}

export function setPublicBrandingQueryData(
  queryClient: QueryClient,
  config: PublicBrandingConfig | BrandingSettings,
) {
  const settings = normalizeBrandingSettings(config)
  cacheBrandingSettings(settings)
  queryClient.setQueryData(publicBrandingQueryKey, settings)
}

function resolvePageLabel(pathname: string, hubAgentName: string, t: TranslationFn) {
  const exactRoutes: [string, string][] = [
    ['/apps', 'userNavigation.appMarket'],
    ['/chat/new', 'userNavigation.newChat'],
    ['/admin/overview', 'adminNavigation.overview'],
    ['/admin/apps', 'adminNavigation.apps'],
    ['/admin/app-review', 'adminNavigation.appReview'],
    ['/admin/app-tags', 'adminNavigation.appTags'],
    ['/admin/model-provider', 'adminNavigation.modelProvider'],
    ['/admin/model-keys', 'adminNavigation.modelKeys'],
    ['/admin/organization/departments', 'adminNavigation.departments'],
    ['/admin/organization/sync', 'adminNavigation.organizationSync'],
    ['/admin/members', 'adminNavigation.members'],
    ['/admin/settings/admins', 'adminNavigation.adminAccounts'],
    ['/admin/settings/sso', 'adminNavigation.sso'],
    ['/admin/idp/provider', 'adminNavigation.idpProvider'],
    ['/admin/idp/resources', 'adminNavigation.idpResources'],
    ['/admin/idp/clients', 'adminNavigation.idpClients'],
    ['/admin/idp/client-scopes', 'adminNavigation.idpClientScopes'],
    ['/admin/credentials/adp', 'adminNavigation.adpCredentials'],
    ['/admin/credentials/wecom', 'adminNavigation.wecomIdentityProfiles'],
    ['/admin/settings/branding', 'adminNavigation.branding'],
    ['/admin/settings/business-navigation', 'adminNavigation.businessNavigation'],
  ]

  if (
    pathname === '/chat' ||
    pathname === '/admin/agent/hub' ||
    pathname === '/admin/settings/core-agent'
  ) {
    return hubAgentName
  }
  const match = exactRoutes.find(([path]) => pathname === path)

  if (match) {
    return t(match[1])
  }

  if (pathname.startsWith('/chat')) {
    return hubAgentName
  }

  if (pathname.startsWith('/admin')) {
    return t('adminWorkspace')
  }

  return undefined
}

export function resolveBrandingDocumentTitle(
  pathname: string,
  siteTitle: string,
  hubAgentName: string,
  t: TranslationFn,
) {
  const pageLabel = resolvePageLabel(pathname, hubAgentName, t)

  return pageLabel ? `${pageLabel} | ${siteTitle}` : siteTitle
}
